import { useState } from 'react';
import { Modal, Button, Form, Alert, Table } from 'react-bootstrap';

const CashSessionClose = ({ show, onClose, onSubmit, session }) => {
  const [formData, setFormData] = useState({
    monto_real: '',
    observaciones: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Safely convert to number
  const toNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const openingAmount = toNumber(session?.monto_inicial);
  const cashSales = toNumber(session?.monto_ventas_efectivo);
  const additionalIncome = toNumber(session?.monto_ingresos);
  const withdrawals = toNumber(session?.monto_retiros);
  const expectedAmount = toNumber(session?.resumen?.monto_esperado);
  const actualAmount = toNumber(formData.monto_real);
  const difference = actualAmount - expectedAmount;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.monto_real || parseFloat(formData.monto_real) < 0) {
      setError('You must enter the actual counted amount');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await onSubmit({
        monto_real: parseFloat(formData.monto_real),
        observaciones: formData.observaciones,
      });

      if (!result.success) {
        setError(result.message || 'Error closing the session');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error closing the session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-door-closed me-2"></i>
          Close Register
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Session Summary */}
          <div className="mb-4">
            <h6 className="mb-3">Session Summary</h6>
            <Table bordered size="sm">
              <tbody>
                <tr>
                  <td>
                    <strong>Opening Amount:</strong>
                  </td>
                  <td className="text-end">${openingAmount.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Cash Sales:</strong>
                  </td>
                  <td className="text-end text-success">+ ${cashSales.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Additional Income:</strong>
                  </td>
                  <td className="text-end text-success">+ ${additionalIncome.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Withdrawals:</strong>
                  </td>
                  <td className="text-end text-danger">- ${withdrawals.toFixed(2)}</td>
                </tr>
                <tr className="table-primary">
                  <td>
                    <strong>Expected Amount:</strong>
                  </td>
                  <td className="text-end fw-bold">${expectedAmount.toFixed(2)}</td>
                </tr>
              </tbody>
            </Table>
          </div>

          <hr />

          {/* Close Session Form */}
          <Form.Group className="mb-3">
            <Form.Label>
              Actual Counted Amount <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              name="monto_real"
              value={formData.monto_real}
              onChange={handleChange}
              placeholder="0.00"
              required
              autoFocus
            />
            <Form.Text className="text-muted">Count the physical cash in the drawer</Form.Text>
          </Form.Group>

          {/* Show Difference */}
          {formData.monto_real && (
            <div className={`alert ${difference === 0 ? 'alert-success' : difference > 0 ? 'alert-warning' : 'alert-danger'}`}>
              <strong>Difference: </strong>
              {difference > 0 && '+ '}
              ${Math.abs(difference).toFixed(2)}
              {difference > 0 && ' (Overage)'}
              {difference < 0 && ' (Shortage)'}
              {difference === 0 && ' (Balanced ✓)'}
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="observaciones"
              value={formData.observaciones}
              onChange={handleChange}
              placeholder="Notes about the closing (optional)"
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Closing...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Close Register
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CashSessionClose;